import { useCallback, useEffect, useState } from 'react';
import {
  api,
  saveFile,
  type ActivityReportView,
  type AnnualObligation,
  type CitAnnual,
  type AnnualReport,
  type Company,
  type FiscalYear,
  type IntroForm,
  type Officer,
  type OfficerRole,
  type RdfPackage,
  type RegisterStatus,
  type Resolution,
  type ManualPosition,
  type ManualStatementView,
  type ReportSource,
  type ResolutionPlan,
  type Shareholder,
  type StatementTemplate,
  type SignatureCapacity,
  type SignatureMethod,
  type SignaturePanel,
} from '../api';
import type { Dictionary, Locale } from '../i18n';
import { date as written } from '../format';
import * as Icon from '../components/Icons';
import { ErrorAlert, failureOf, type Failure } from '../components/Alerts';
import type { Panel } from '../components/Shell';

/**
 * Sprawozdanie roczne spółki do KRS (docs/08).
 *
 * Ekran jest kreatorem, ale nie prowadzi za rękę krok po kroku: sprawozdanie robi się tygodniami,
 * z przerwami, i rzadko w kolejności z projektu — zarząd podpisuje, gdy wróci z urlopu, a uchwały
 * czekają na zgromadzenie. Dlatego kroki są **zakładkami**, a nie „dalej / wstecz": każdą można
 * otworzyć w dowolnym momencie i każda sama mówi, czego jeszcze brakuje.
 *
 * Dwie rzeczy, których ten ekran **nie robi**:
 *
 * 1. **Nie ma pola na kwotę.** Ani bilansu, ani rachunku wyników, ani kapitału. Kwoty liczy
 *    projekcja z ksiąg (docs/08 §4), a zmiana idzie przez dziennik — jedyne pole, które by je
 *    zmieniło, byłoby polem na pomyłkę bez śladu.
 * 2. **Nie udaje, że coś jest sprawdzone.** Przy podpisach, przy wzmiankach w rejestrze i przy
 *    liście kontrolnej kompletu pokazujemy zdania z serwera o tym, czego nie sprawdziliśmy —
 *    zielony znaczek bez pokrycia jest gorszy niż jego brak.
 */

type Tab =
  | 'year'
  | 'amounts'
  | 'form'
  | 'file'
  | 'activity'
  | 'signatures'
  | 'resolutions'
  | 'krs'
  | 'registers';

const PREPARERS = ['OWNER', 'ADMIN', 'ACCOUNTANT'];
const SIGNERS = ['OWNER', 'ADMIN', 'ACCOUNTANT', 'BOARD_MEMBER'];

export function AnnualPanel({
  company,
  t,
  locale,
  onGo,
}: {
  company: Company;
  t: Dictionary;
  locale: Locale;
  onGo: (panel: Panel) => void;
}) {
  const [years, setYears] = useState<FiscalYear[] | null>(null);
  const [reports, setReports] = useState<AnnualReport[]>([]);
  const [yearId, setYearId] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('year');
  /** Tryb i wzór wybierane przy zakładaniu sprawozdania — potem zmienia je tylko nowa wersja. */
  const [mode, setMode] = useState<ReportSource>('BOOKS');
  const [template, setTemplate] = useState<StatementTemplate>('ZAL_5');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Failure | null>(null);

  const load = useCallback(async () => {
    try {
      const [fiscal, list] = await Promise.all([api.fiscalYears(company.id), api.annualReports(company.id)]);
      setYears(fiscal);
      setReports(list);
      // Domyślnie rok, który ma już sprawozdanie, a jeśli żaden nie ma — ostatni zakończony:
      // o ten właśnie chodzi, gdy ktoś otwiera ten ekran w marcu
      setYearId((current) => current ?? list[0]?.fiscalYearId ?? lastEnded(fiscal)?.id ?? fiscal[0]?.id ?? null);
    } catch (failure) {
      setError(failureOf(failure));
    }
  }, [company.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const editable = company.roles.some((role) => PREPARERS.includes(role));
  const year = years?.find((item) => item.id === yearId) ?? null;
  // Lista przychodzi od najnowszej wersji, więc pierwsze trafienie to bieżąca wersja tego roku
  const report = reports.find((item) => item.fiscalYearId === yearId) ?? null;
  const versions = reports.filter((item) => item.fiscalYearId === yearId);

  async function start() {
    if (!yearId) return;
    setBusy(true);
    setError(null);
    try {
      await api.createAnnualReport(company.id, yearId, {
        source: mode,
        // Wzór wybiera człowiek tylko wtedy, gdy nie ma planu kont, z którego dałoby się go wziąć
        statementTemplate: mode === 'BOOKS' ? undefined : template,
      });
      await load();
      setTab(mode === 'BOOKS' ? 'form' : 'amounts');
    } catch (failure) {
      setError(failureOf(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="hd">
        <div>
          <h3>{t.annual.title}</h3>
          <p>{t.annual.lead}</p>
        </div>
      </div>

      <ErrorAlert error={error} t={t} onGo={onGo} />

      {years?.length === 0 && (
        <div className="panel">
          <div className="empty">
            <Icon.Calendar />
            <b>{t.annual.year.noYears}</b>
            <p>{t.annual.year.noYearsHint}</p>
            <div className="row" style={{ justifyContent: 'center' }}>
              <button type="button" className="btn sec" onClick={() => onGo('periods')}>
                {t.periods.open}
              </button>
            </div>
          </div>
        </div>
      )}

      {years && years.length > 0 && (
        <div className="panel">
          <header>
            <h4>{t.annual.year.pick}</h4>
            <div className="sp">
              <select
                className="inp w"
                aria-label={t.annual.year.pick}
                value={yearId ?? ''}
                onChange={(event) => setYearId(event.target.value)}
              >
                {years.map((item) => (
                  <option key={item.id} value={item.id}>
                    {yearLabel(item)}
                  </option>
                ))}
              </select>
            </div>
          </header>

          {/* Stan roku obrotowego stoi tu obok stanu sprawozdania, bo to on tłumaczy znak
              „wersja robocza": z otwartego roku sprawozdanie wolno złożyć, ale musi o tym mówić */}
          {year && (
            <div className="row">
              <span className={year.status === 'CLOSED' ? 'chip st-draft' : 'chip st-ok'}>
                <i />
                {year.status === 'CLOSED' ? t.periods.closed : t.periods.open_}
              </span>
            </div>
          )}

          {report ? (
            <div className="row">
              <span className={`chip ${statusChip(report.status)}`}>
                <i />
                {t.annual.statuses[report.status]}
              </span>
              <span className="muted sm">{t.annual.year.version(report.version)}</span>
              {report.draftMark && (
                <span className="chip st-warn" title={t.annual.year.draftMarkHint}>
                  <i />
                  {t.annual.year.draftMark}
                </span>
              )}
              {report.stale && (
                <span className="chip st-warn" title={t.annual.year.staleHint}>
                  <i />
                  {t.annual.year.stale}
                </span>
              )}
            </div>
          ) : (
            <div className="empty">
              <Icon.Balance />
              <b>{t.annual.year.noReport}</b>
              <p>{t.annual.year.noReportHint}</p>
              {editable && (
                <>
                  {/* Tryb ustala się **raz**, przy zakładaniu: zmiana trybu to nowa wersja z powodem,
                      nigdy podmiana kwot w istniejącym sprawozdaniu (docs/08, ścieżka skrócona) */}
                  <div className="fields">
                    <div className="field">
                      <label htmlFor="mode">{t.annual.modes.title}</label>
                      <select
                        id="mode"
                        className="inp"
                        value={mode}
                        onChange={(event) => setMode(event.target.value as ReportSource)}
                      >
                        <option value="BOOKS">{t.annual.modes.BOOKS}</option>
                        <option value="DORMANT">{t.annual.modes.DORMANT}</option>
                        <option value="MANUAL">{t.annual.modes.MANUAL}</option>
                      </select>
                      <span className="hint">
                        {mode === 'BOOKS'
                          ? t.annual.modes.booksHint
                          : mode === 'DORMANT'
                            ? t.annual.modes.dormantHint
                            : t.annual.modes.manualHint}
                      </span>
                    </div>
                    {mode !== 'BOOKS' && (
                      <div className="field">
                        <label htmlFor="template">{t.annual.modes.template}</label>
                        <select
                          id="template"
                          className="inp"
                          value={template}
                          onChange={(event) => setTemplate(event.target.value as StatementTemplate)}
                        >
                          <option value="ZAL_4">{t.annual.modes.templates.ZAL_4}</option>
                          <option value="ZAL_5">{t.annual.modes.templates.ZAL_5}</option>
                          <option value="ZAL_1">{t.annual.modes.templates.ZAL_1}</option>
                        </select>
                      </div>
                    )}
                  </div>
                  <div className="row" style={{ justifyContent: 'center' }}>
                    <button type="button" className="btn" disabled={busy || !yearId} onClick={() => void start()}>
                      {t.annual.year.start}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      <div className="panel">
        <div className="tabs" role="tablist">
          {(
            [
              ['year', t.annual.tabs.year],
              // Zakładka kwot istnieje tylko dla sprawozdania spoza ksiąg — przy `books` nie ma
              // czego na niej robić, a pole na kwotę obok ksiąg byłoby polem na pomyłkę bez śladu
              ...(report && report.source !== 'books' ? ([['amounts', t.annual.amounts.title]] as [Tab, string][]) : []),
              ['form', t.annual.tabs.form],
              ['file', t.annual.tabs.file],
              ['activity', t.annual.activity.title],
              ['signatures', t.annual.tabs.signatures],
              ['resolutions', t.annual.tabs.resolutions],
              ['krs', t.annual.tabs.krs],
              ['registers', t.annual.tabs.registers],
            ] as [Tab, string][]
          ).map(([key, label]) => (
            <button type="button" role="tab" key={key} aria-selected={tab === key} onClick={() => setTab(key)}>
              {label}
            </button>
          ))}
        </div>

        {tab === 'year' && (
          <YearTab
            companyId={company.id}
            yearId={yearId}
            versions={versions}
            t={t}
            locale={locale}
          />
        )}
        {tab === 'registers' && <RegistersTab companyId={company.id} editable={editable} t={t} locale={locale} />}

        {/* Reszta zakładek dotyczy konkretnego sprawozdania — bez niego nie ma o czym mówić */}
        {tab !== 'year' && tab !== 'registers' && !report && (
          <div className="empty">
            <b>{t.annual.year.noReport}</b>
            <p>{t.annual.year.noReportHint}</p>
          </div>
        )}

        {report && tab === 'amounts' && (
          <AmountsTab companyId={company.id} report={report} editable={editable} t={t} onChanged={load} />
        )}
        {report && tab === 'form' && (
          <FormTab companyId={company.id} report={report} editable={editable} t={t} />
        )}
        {report && tab === 'file' && (
          <FileTab
            companyId={company.id}
            report={report}
            editable={editable}
            t={t}
            locale={locale}
            onChanged={load}
          />
        )}
        {report && tab === 'activity' && (
          <ActivityTab companyId={company.id} report={report} editable={editable} t={t} onChanged={load} />
        )}
        {report && tab === 'signatures' && (
          <SignaturesTab
            companyId={company.id}
            report={report}
            canSign={company.roles.some((role) => SIGNERS.includes(role))}
            t={t}
            locale={locale}
            onChanged={load}
          />
        )}
        {report && tab === 'resolutions' && (
          <ResolutionsTab
            companyId={company.id}
            report={report}
            editable={editable}
            t={t}
            locale={locale}
            onChanged={load}
          />
        )}
        {report && tab === 'krs' && (
          <KrsTab companyId={company.id} report={report} editable={editable} t={t} locale={locale} onChanged={load} />
        )}
      </div>
    </>
  );
}

/** Rok obrotowy, terminarz i historia wersji — to, co widać, zanim zacznie się cokolwiek robić. */
function YearTab({
  companyId,
  yearId,
  versions,
  t,
  locale,
}: {
  companyId: string;
  yearId: string | null;
  versions: AnnualReport[];
  t: Dictionary;
  locale: Locale;
}) {
  const [obligations, setObligations] = useState<AnnualObligation[]>([]);
  const [blankTemplate, setBlankTemplate] = useState<StatementTemplate>('ZAL_5');
  /** Rozliczenie CIT-8; null, gdy firma nie płaci CIT — wtedy bloku nie pokazujemy wcale. */
  const [cit, setCit] = useState<CitAnnual | null>(null);
  const [priorLoss, setPriorLoss] = useState('');
  const [error, setError] = useState<Failure | null>(null);

  useEffect(() => {
    if (!yearId) return;
    void (async () => {
      try {
        setObligations((await api.annualCalendar(companyId, yearId)).obligations);
      } catch (failure) {
        setError(failureOf(failure));
      }
      // Odmowa znaczy „ta firma nie płaci CIT" i nie jest błędem do pokazania
      setCit(await api.cit8(companyId, yearId).catch(() => null));
    })();
  }, [companyId, yearId]);

  return (
    <>
      <ErrorAlert error={error} t={t} />

      <h4>{t.annual.year.calendar}</h4>
      <p className="hint">{t.annual.year.calendarHint}</p>

      {obligations.length > 0 && (
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th>{t.annual.year.calendar}</th>
                <th>{t.annual.year.statutory}</th>
                <th>{t.annual.year.safe}</th>
                <th>{t.annual.year.state}</th>
              </tr>
            </thead>
            <tbody>
              {obligations.map((item) => (
                <tr key={`${item.kind}-${item.fiscalYearId}`}>
                  <td>
                    {t.annual.year.obligations[item.kind]}
                    {item.note && <span className="muted sm"> {item.note}</span>}
                  </td>
                  <td className="doc">{written(item.statutoryDate, locale)}</td>
                  <td className="doc">{written(item.safeDate, locale)}</td>
                  <td>
                    {item.done ? (
                      <span className="chip st-ok">
                        <i />
                        {t.annual.year.done}
                        {item.doneOn && ` · ${written(item.doneOn, locale)}`}
                      </span>
                    ) : item.overdue ? (
                      <span className="chip st-bad">
                        <i />
                        {t.annual.year.overdue}
                      </span>
                    ) : (
                      <span className="chip st-info">
                        <i />
                        {item.daysLeft === null ? '—' : t.annual.year.daysLeft(item.daysLeft)}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {cit && (
        <>
          <h4>{t.annual.cit.title}</h4>
          <p className="hint">{t.annual.cit.hint}</p>
          <div className="tbl">
            <table>
              <tbody>
                <tr>
                  <td>{t.annual.cit.base}</td>
                  <td className="n mono">{cit.base}</td>
                </tr>
                <tr>
                  <td>{t.annual.cit.rate}</td>
                  <td className="n mono">{(Number(cit.rate) * 100).toFixed(0)}%</td>
                </tr>
                <tr>
                  <td>{t.annual.cit.taxForYear}</td>
                  <td className="n mono">{cit.taxForYear}</td>
                </tr>
                <tr>
                  <td>{t.annual.cit.fromAdvances}</td>
                  <td className="n mono">{cit.taxFromAdvances}</td>
                </tr>
                <tr>
                  <td>
                    <b>{t.annual.cit.difference}</b>
                  </td>
                  <td className="n mono">
                    <b>{cit.difference}</b>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="bar2">
            <div className="field">
              <label htmlFor="priorLoss">{t.annual.cit.priorLoss}</label>
              <input
                id="priorLoss"
                className="inp mono"
                inputMode="decimal"
                value={priorLoss}
                onChange={(event) => setPriorLoss(event.target.value)}
              />
            </div>
            <div className="sp">
              <button
                type="button"
                className="btn sec"
                onClick={() =>
                  void api
                    .cit8(companyId, yearId ?? '', priorLoss.replace(',', '.') || undefined)
                    .then(setCit)
                    .catch((failure) => setError(failureOf(failure)))
                }
              >
                {t.annual.cit.recalculate}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Pusty wzór stoi przy roku, a nie przy sprawozdaniu: bierze się go **zanim** cokolwiek
          powstanie — żeby zebrać kwoty od klienta, który dopiero przyszedł */}
      <h4>{t.annual.blank.title}</h4>
      <p className="hint">{t.annual.blank.hint}</p>
      <div className="bar2">
        <select
          className="inp w"
          aria-label={t.annual.blank.title}
          value={blankTemplate}
          onChange={(event) => setBlankTemplate(event.target.value as StatementTemplate)}
        >
          <option value="ZAL_4">{t.annual.modes.templates.ZAL_4}</option>
          <option value="ZAL_5">{t.annual.modes.templates.ZAL_5}</option>
          <option value="ZAL_1">{t.annual.modes.templates.ZAL_1}</option>
        </select>
        <div className="sp">
          <button
            type="button"
            className="btn sec"
            onClick={() =>
              void api
                .statementBlank(companyId, blankTemplate, 'pdf')
                .then(saveFile)
                .catch((failure) => setError(failureOf(failure)))
            }
          >
            <Icon.Download />
            {t.annual.blank.pdf}
          </button>
          <button
            type="button"
            className="btn ghost"
            onClick={() =>
              void api
                .statementBlank(companyId, blankTemplate, 'xlsx')
                .then(saveFile)
                .catch((failure) => setError(failureOf(failure)))
            }
          >
            <Icon.Download />
            {t.annual.blank.xlsx}
          </button>
        </div>
      </div>

      {versions.length > 0 && (
        <>
          <h4>{t.annual.year.versions}</h4>
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th className="n">{t.annual.year.versions}</th>
                  <th>{t.annual.year.state}</th>
                  <th>{t.annual.year.versionReason}</th>
                  <th>{t.annual.signatures.deadline}</th>
                </tr>
              </thead>
              <tbody>
                {versions.map((item) => (
                  <tr key={item.id}>
                    <td className="n">{item.version}</td>
                    <td>
                      <span className={`chip ${statusChip(item.status)}`}>
                        <i />
                        {t.annual.statuses[item.status]}
                      </span>
                    </td>
                    <td className="muted sm">{item.versionReason ?? '—'}</td>
                    <td className="doc">{item.preparedOn ? written(item.preparedOn, locale) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}

/**
 * Kwoty wpisywane ręcznie — ścieżka skrócona (docs/08).
 *
 * Ekran wygląda jak sprawozdanie, bo nim jest: te same pozycje w tej samej kolejności. Wpisać da
 * się tylko w pozycje składowe; sumy i pozycje liczone formułą stoją obok wyszarzone i zmieniają
 * się po zapisie. Różnica bilansowa jest na wierzchu przez cały czas — to ona mówi, czy z tego
 * sprawozdania powstanie plik.
 */
function AmountsTab({
  companyId,
  report,
  editable,
  t,
  onChanged,
}: {
  companyId: string;
  report: AnnualReport;
  editable: boolean;
  t: Dictionary;
  onChanged: () => Promise<void>;
}) {
  const [view, setView] = useState<ManualStatementView | null>(null);
  /** Tylko to, co człowiek zmienił — nie wysyłamy stu zer, których nikt nie wpisał. */
  const [changed, setChanged] = useState<Record<string, { amount?: string; previousAmount?: string }>>({});
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Failure | null>(null);

  const load = useCallback(async () => {
    try {
      setView(await api.manualAmounts(companyId, report.id));
    } catch (failure) {
      setError(failureOf(failure));
    }
  }, [companyId, report.id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function save() {
    if (!view) return;
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const entries = Object.entries(changed).map(([positionCode, value]) => {
        const position = view.positions.find((item) => item.code === positionCode);
        return {
          positionCode,
          amount: (value.amount ?? position?.amount ?? '0').replace(',', '.') || '0',
          previousAmount: value.previousAmount?.replace(',', '.') || position?.previousAmount || null,
        };
      });
      setView(await api.saveManualAmounts(companyId, report.id, entries));
      setChanged({});
      setSaved(true);
      await onChanged();
    } catch (failure) {
      setError(failureOf(failure));
    } finally {
      setBusy(false);
    }
  }

  if (!view) return <p className="muted">{t.loading}</p>;

  const value = (position: ManualPosition) => changed[position.code]?.amount ?? position.amount;
  const previous = (position: ManualPosition) =>
    changed[position.code]?.previousAmount ?? position.previousAmount ?? '';

  return (
    <>
      <ErrorAlert error={error} t={t} />
      <h4>{t.annual.amounts.title}</h4>
      <p className="hint">{t.annual.amounts.lead}</p>

      <div className="row">
        <span className={`chip ${view.balanced ? 'st-ok' : 'st-warn'}`}>
          <i />
          {view.balanced ? t.annual.amounts.balanced : t.annual.amounts.unbalanced}
        </span>
        <span className="muted sm">
          {t.annual.amounts.totals}: <span className="mono">{view.totalAssets}</span> ·{' '}
          <span className="mono">{view.totalLiabilities}</span> · <span className="mono">{view.result}</span>
        </span>
        {!view.balanced && (
          <span className="muted sm">
            {t.annual.amounts.difference}: <span className="mono">{view.difference}</span>
          </span>
        )}
      </div>

      {view.notes.map((note) => (
        <p className="hint" key={note}>
          {note}
        </p>
      ))}
      {saved && (
        <p className="hint" role="status">
          {t.annual.amounts.saved}
        </p>
      )}

      <div className="tbl">
        <table>
          <thead>
            <tr>
              <th>{t.annual.amounts.position}</th>
              <th className="n">{t.annual.amounts.amount}</th>
              <th className="n">{t.annual.amounts.previous}</th>
            </tr>
          </thead>
          <tbody>
            {view.positions.map((position) => (
              <tr key={position.code}>
                <td style={{ paddingLeft: `${position.level * 12}px` }}>
                  {position.label}
                  <span className="muted sm mono"> {position.code.split('.').pop()}</span>
                </td>
                <td className="n">
                  {position.editable && editable ? (
                    <input
                      className="inp mono sm"
                      inputMode="decimal"
                      aria-label={`${t.annual.amounts.amount}: ${position.label}`}
                      value={value(position)}
                      onChange={(event) =>
                        setChanged((current) => ({
                          ...current,
                          [position.code]: { ...current[position.code], amount: event.target.value },
                        }))
                      }
                    />
                  ) : (
                    <span className="mono muted">{position.amount}</span>
                  )}
                </td>
                <td className="n">
                  {position.editable && editable ? (
                    <input
                      className="inp mono sm"
                      inputMode="decimal"
                      aria-label={`${t.annual.amounts.previous}: ${position.label}`}
                      value={previous(position)}
                      onChange={(event) =>
                        setChanged((current) => ({
                          ...current,
                          [position.code]: { ...current[position.code], previousAmount: event.target.value },
                        }))
                      }
                    />
                  ) : (
                    <span className="mono muted">{position.previousAmount ?? '—'}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editable && (
        <div className="bar2">
          <button
            type="button"
            className="btn"
            disabled={busy || Object.keys(changed).length === 0}
            onClick={() => void save()}
          >
            {t.annual.amounts.save}
          </button>
        </div>
      )}
    </>
  );
}

/**
 * Wprowadzenie i informacja dodatkowa.
 *
 * Pola przychodzą ze słownika wzoru, a nie z listy w kodzie: gdy Ministerstwo wyda nową wersję
 * schemy, ekran ma pokazać jej pola, a nie nasze wspomnienie o poprzedniej.
 */
function FormTab({
  companyId,
  report,
  editable,
  t,
}: {
  companyId: string;
  report: AnnualReport;
  editable: boolean;
  t: Dictionary;
}) {
  const [form, setForm] = useState<IntroForm | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<Failure | null>(null);

  const load = useCallback(async () => {
    try {
      const loaded = await api.annualForm(companyId, report.id);
      setForm(loaded);
      setValues(Object.fromEntries(loaded.answers.map((answer) => [`${answer.section}/${answer.code}`, answer.value])));
    } catch (failure) {
      setError(failureOf(failure));
    }
  }, [companyId, report.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const put = (section: string, code: string, value: string) =>
    setValues((current) => ({ ...current, [`${section}/${code}`]: value }));
  const get = (section: string, code: string) => values[`${section}/${code}`] ?? '';

  async function save() {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const entries = Object.entries(values).map(([key, value]) => {
        const [section, code] = key.split('/');
        return { section: section as 'INTRODUCTION' | 'NOTES' | 'SEAT' | 'TAX', code, value };
      });
      await api.saveAnnualAnswers(companyId, report.id, entries);
      setSaved(true);
      await load();
    } catch (failure) {
      setError(failureOf(failure));
    } finally {
      setBusy(false);
    }
  }

  if (!form) return <p className="muted">{t.loading}</p>;

  const missing = new Set(form.missing.map((item) => `${item.section}/${item.code}`));

  return (
    <>
      <ErrorAlert error={error} t={t} />
      <h4>{t.annual.form.title}</h4>
      <p className="hint">{t.annual.form.lead}</p>

      {form.missing.length > 0 ? (
        <div className="alert a-warn">
          <Icon.Warn />
          <div>
            <b>{t.annual.form.missing}</b>
            <p>{form.missing.map((item) => `${item.code} — ${item.label}`).join(' · ')}</p>
          </div>
        </div>
      ) : (
        <p className="hint" role="status">
          {t.annual.form.complete}
        </p>
      )}
      {saved && (
        <p className="hint" role="status">
          {t.annual.form.saved}
        </p>
      )}

      <h4>{t.annual.form.sections.INTRODUCTION}</h4>
      <div className="fields">
        {form.fields.map((field) => (
          <div className="field" key={field.code}>
            <label htmlFor={`f-${field.code}`}>
              {field.label}
              {field.required && ' *'}
              <span className="muted sm mono"> {field.code}</span>
            </label>
            <input
              id={`f-${field.code}`}
              className={`inp${missing.has(`introduction/${field.code}`) ? ' bad' : ''}`}
              value={get('INTRODUCTION', field.code)}
              onChange={(event) => put('INTRODUCTION', field.code, event.target.value)}
              disabled={!editable}
            />
          </div>
        ))}
      </div>

      <h4>{t.annual.form.sections.SEAT}</h4>
      <div className="fields">
        {form.seatCodes.map((code) => (
          <div className="field" key={code}>
            <label htmlFor={`s-${code}`}>{code}</label>
            <input
              id={`s-${code}`}
              className={`inp${missing.has(`seat/${code}`) ? ' bad' : ''}`}
              value={get('SEAT', code)}
              onChange={(event) => put('SEAT', code, event.target.value)}
              disabled={!editable}
            />
          </div>
        ))}
      </div>

      <h4>{t.annual.form.sections.NOTES}</h4>
      <div className="field">
        <label htmlFor="notes">{t.annual.form.sections.NOTES}</label>
        <textarea
          id="notes"
          className="inp"
          rows={5}
          value={get('NOTES', 'Opis')}
          onChange={(event) => put('NOTES', 'Opis', event.target.value)}
          disabled={!editable}
        />
      </div>

      {form.taxCodes.length > 0 && (
        <>
          <h4>{t.annual.form.sections.TAX}</h4>
          <div className="fields">
            {form.taxCodes.map((code) => (
              <div className="field" key={code}>
                <label htmlFor={`x-${code}`}>
                  <span className="mono">{code}</span>
                </label>
                <input
                  id={`x-${code}`}
                  className="inp"
                  value={get('TAX', code)}
                  onChange={(event) => put('TAX', code, event.target.value)}
                  disabled={!editable}
                />
              </div>
            ))}
          </div>
        </>
      )}

      {editable && (
        <div className="bar2">
          <button type="button" className="btn" disabled={busy} onClick={() => void save()}>
            {t.annual.form.save}
          </button>
        </div>
      )}
    </>
  );
}

/** Plik sprawozdania: złożenie z ksiąg, pobranie, nowa wersja. */
function FileTab({
  companyId,
  report,
  editable,
  t,
  locale,
  onChanged,
}: {
  companyId: string;
  report: AnnualReport;
  editable: boolean;
  t: Dictionary;
  locale: Locale;
  onChanged: () => Promise<void>;
}) {
  const [tax, setTax] = useState(false);
  const [reason, setReason] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Failure | null>(null);

  const hasFile = report.files.some((file) => file.role === 'statement_xml');
  // Od pierwszego podpisu plik jest zamknięty na zmiany (docs/08, krok 8). Przycisku, który zawsze
  // skończy się odmową, nie pokazujemy: zostaje droga wyjścia, czyli nowa wersja z powodem
  const rebuildable = report.status === 'draft' || report.status === 'assembled';
  const hasPdf = report.files.some((file) => file.role === 'statement_pdf');

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await onChanged();
    } catch (failure) {
      setError(failureOf(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <ErrorAlert error={error} t={t} />
      <h4>{t.annual.file.title}</h4>
      <p className="hint">{t.annual.file.lead}</p>
      <p className="hint">{t.annual.file.noAmounts}</p>
      <p className="hint">{t.annual.file.pdfHint}</p>

      {editable && (
        <div className="bar2">
          {rebuildable && (
            <label className="check">
              <input type="checkbox" checked={tax} onChange={(event) => setTax(event.target.checked)} />
              {t.annual.file.taxReconciliation}
            </label>
          )}
          <div className="sp">
            {rebuildable && (
              <button
                type="button"
                className="btn"
                disabled={busy}
                onClick={() => void run(() => api.generateStatement(companyId, report.id, tax))}
              >
                {hasFile ? t.annual.file.regenerate : t.annual.file.generate}
              </button>
            )}
            {hasFile && (
              <button
                type="button"
                className="btn sec"
                disabled={busy}
                onClick={() => void run(async () => saveFile(await api.statementFile(companyId, report.id)))}
              >
                <Icon.Download />
                {t.annual.file.download}
              </button>
            )}
            {/* Wydruk wolno złożyć w każdym stanie: po podpisach i po złożeniu w KRS jest potrzebny
                najczęściej — na zgromadzenie, do banku, do teczki */}
            <button
              type="button"
              className="btn sec"
              disabled={busy}
              onClick={() => void run(() => api.generateStatementPdf(companyId, report.id))}
            >
              {t.annual.file.pdf}
            </button>
            {hasPdf && (
              <button
                type="button"
                className="btn ghost"
                disabled={busy}
                onClick={() => void run(async () => saveFile(await api.statementPdfFile(companyId, report.id)))}
              >
                <Icon.Download />
                {t.annual.file.pdfDownload}
              </button>
            )}
            <button type="button" className="btn ghost" onClick={() => setReason(reason === null ? '' : null)}>
              {t.annual.file.newVersion}
            </button>
          </div>
        </div>
      )}

      {reason !== null && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void run(async () => {
              await api.newReportVersion(companyId, report.id, reason);
              setReason(null);
            });
          }}
        >
          <div className="field">
            <label htmlFor="versionReason">{t.annual.file.reason}</label>
            <input
              id="versionReason"
              className="inp"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              required
            />
            <span className="hint">{t.annual.file.reasonHint}</span>
          </div>
          <div className="bar2">
            <button type="submit" className="btn" disabled={busy || reason.trim() === ''}>
              {t.annual.file.newVersion}
            </button>
          </div>
        </form>
      )}

      {report.files.length > 0 && (
        <>
          <h4>{t.annual.file.files}</h4>
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th>{t.annual.file.role}</th>
                  <th>{t.annual.file.generatedAt}</th>
                  <th>{t.annual.file.sha256}</th>
                </tr>
              </thead>
              <tbody>
                {report.files.map((file) => (
                  <tr key={file.id}>
                    <td>{t.annual.file.roles[file.role]}</td>
                    <td className="doc">{written(file.generatedAt, locale)}</td>
                    <td className="mono sm muted">{file.sha256.slice(0, 16)}…</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}

/**
 * Sprawozdanie zarządu z działalności (docs/08, sekcja 7).
 *
 * Ekran ma trzy warstwy i każda odpowiada na inne pytanie: **wskaźniki** mówią, jak spółce poszło
 * (i z czego to liczymy), **propozycje z ksiąg** podsuwają akapity, o których zarząd zwykle
 * zapomina, a **sekcje** są dokumentem. Propozycja dopisuje się do sekcji i dopiero tam staje się
 * treścią — nic nie wchodzi do dokumentu bez przeczytania przez człowieka.
 */
function ActivityTab({
  companyId,
  report,
  editable,
  t,
  onChanged,
}: {
  companyId: string;
  report: AnnualReport;
  editable: boolean;
  t: Dictionary;
  onChanged: () => Promise<void>;
}) {
  const [view, setView] = useState<ActivityReportView | null>(null);
  const [texts, setTexts] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Failure | null>(null);

  const load = useCallback(async () => {
    try {
      const loaded = await api.activityReport(companyId, report.id);
      setView(loaded);
      setTexts(Object.fromEntries(loaded.sections.map((section) => [section.code, section.text ?? ''])));
    } catch (failure) {
      setError(failureOf(failure));
    }
  }, [companyId, report.id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await action();
      await load();
      await onChanged();
    } catch (failure) {
      setError(failureOf(failure));
    } finally {
      setBusy(false);
    }
  }

  if (!view) return <p className="muted">{t.loading}</p>;

  return (
    <>
      <ErrorAlert error={error} t={t} />
      <h4>{t.annual.activity.title}</h4>
      <p className="hint">{t.annual.activity.lead}</p>

      <div className="row">
        <span className={`chip ${view.ready ? 'st-ok' : 'st-warn'}`}>
          <i />
          {view.ready ? t.annual.activity.ready : t.annual.activity.missing}
        </span>
        {view.signers.length > 0 && (
          <span className="muted sm">
            {t.annual.activity.signers}: {view.signers.join(', ')}
          </span>
        )}
      </div>
      {view.missing.length > 0 && (
        <p className="hint">
          {t.annual.activity.missing} {view.missing.join(' · ')}
        </p>
      )}
      {view.notes.map((note) => (
        <p className="hint" key={note}>
          {note}
        </p>
      ))}

      <h4>{t.annual.activity.indicators}</h4>
      <div className="tbl">
        <table>
          <thead>
            <tr>
              <th>{t.annual.activity.indicator}</th>
              <th className="n">{t.annual.activity.value}</th>
              <th>{t.annual.activity.basis}</th>
            </tr>
          </thead>
          <tbody>
            {view.indicators.map((indicator) => (
              <tr key={indicator.code}>
                <td>{indicator.label}</td>
                {/* Wskaźnik bez wartości pokazujemy razem ze zdaniem, dlaczego nie ma sensu —
                    kreska bez wyjaśnienia trafiłaby do dokumentu jako liczba */}
                <td className="n mono">{indicator.value ?? '—'}</td>
                <td className="muted sm">{indicator.explanation ?? indicator.basis}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {view.suggestions.length > 0 && (
        <>
          <h4>{t.annual.activity.suggestions}</h4>
          <p className="hint">{t.annual.activity.suggestionsHint}</p>
          {view.suggestions.map((suggestion) => (
            <div className="alert a-info" key={suggestion.code}>
              <Icon.Info />
              <div>
                <b>{suggestion.text}</b>
                <p className="muted sm">{suggestion.basis}</p>
                {editable && (
                  <button
                    type="button"
                    className="btn ghost sm"
                    onClick={() =>
                      setTexts((current) => ({
                        ...current,
                        [suggestion.section]: [current[suggestion.section], suggestion.text]
                          .filter((part) => part && part.trim() !== '')
                          .join('\n\n'),
                      }))
                    }
                  >
                    {t.annual.activity.add}
                  </button>
                )}
              </div>
            </div>
          ))}
        </>
      )}

      <h4>{t.annual.activity.sections}</h4>
      {view.sections.map((section) => (
        <div className="field" key={section.code}>
          <label htmlFor={`sec-${section.code}`}>
            {section.code}. {section.title}
            <span className="muted sm">
              {' '}
              {section.required ? t.annual.activity.required : t.annual.activity.optional}
              {section.fromData && ` · ${t.annual.activity.fromData}`}
              {section.source === 'generated' && ` · ${t.annual.activity.proposal}`}
            </span>
          </label>
          <textarea
            id={`sec-${section.code}`}
            className="inp"
            rows={section.fromData ? 2 : 4}
            value={texts[section.code] ?? ''}
            onChange={(event) => setTexts((current) => ({ ...current, [section.code]: event.target.value }))}
            disabled={!editable}
          />
          {section.legalBasis && <span className="hint">{section.legalBasis}</span>}
        </div>
      ))}

      {saved && (
        <p className="hint" role="status">
          {t.annual.activity.saved}
        </p>
      )}

      {editable && (
        <div className="bar2">
          <button
            type="button"
            className="btn"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                await api.saveActivityReport(
                  companyId,
                  report.id,
                  Object.entries(texts).map(([code, text]) => ({ code, text })),
                );
                setSaved(true);
              })
            }
          >
            {t.annual.activity.save}
          </button>
          <div className="sp">
            <button
              type="button"
              className="btn sec"
              disabled={busy}
              onClick={() => void run(() => api.renderActivityReport(companyId, report.id, 'pdf'))}
            >
              {t.annual.activity.pdf}
            </button>
            <button
              type="button"
              className="btn ghost"
              disabled={busy}
              onClick={() => void run(() => api.renderActivityReport(companyId, report.id, 'docx'))}
            >
              {t.annual.activity.docx}
            </button>
            {report.files.some((file) => file.role === 'activity_report') && (
              <button
                type="button"
                className="btn ghost"
                disabled={busy}
                onClick={() => void run(async () => saveFile(await api.activityReportFile(companyId, report.id)))}
              >
                <Icon.Download />
                {t.annual.activity.download}
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Podpisy (docs/08, krok 8).
 *
 * Ekran mówi trzy rzeczy, których nie da się wyczytać z samej listy osób: **który plik** pobrać
 * do podpisu (ten z podpisami poprzedników), **czego nie sprawdziliśmy** w podpisach już zebranych
 * i **kto podpisał, choć nie był w zarządzie** — to nie błąd techniczny, ale formalny.
 */
function SignaturesTab({
  companyId,
  report,
  canSign,
  t,
  locale,
  onChanged,
}: {
  companyId: string;
  report: AnnualReport;
  canSign: boolean;
  t: Dictionary;
  locale: Locale;
  onChanged: () => Promise<void>;
}) {
  const [panel, setPanel] = useState<SignaturePanel | null>(null);
  const [officers, setOfficers] = useState<Officer[]>([]);
  const [capacity, setCapacity] = useState<SignatureCapacity>('BOARD_MEMBER');
  const [method, setMethod] = useState<SignatureMethod>('QUALIFIED');
  const [officerId, setOfficerId] = useState('');
  const [personName, setPersonName] = useState('');
  const [note, setNote] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [declarationKind, setDeclarationKind] = useState<'STATEMENT' | 'REFUSAL'>('STATEMENT');
  const [declarationOfficer, setDeclarationOfficer] = useState('');
  const [declarationNote, setDeclarationNote] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Failure | null>(null);

  const load = useCallback(async () => {
    try {
      const [signatures, board] = await Promise.all([
        api.signaturePanel(companyId, report.id),
        api.officers(companyId),
      ]);
      setPanel(signatures);
      setOfficers(board);
    } catch (failure) {
      setError(failureOf(failure));
    }
  }, [companyId, report.id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
      await onChanged();
    } catch (failure) {
      setError(failureOf(failure));
    } finally {
      setBusy(false);
    }
  }

  if (!panel) return <p className="muted">{t.loading}</p>;

  return (
    <>
      <ErrorAlert error={error} t={t} />
      <h4>{t.annual.signatures.title}</h4>
      <p className="hint">{t.annual.signatures.lead}</p>

      <div className="row">
        <span className={`chip ${panel.complete ? 'st-ok' : 'st-warn'}`}>
          <i />
          {panel.complete ? t.annual.signatures.complete : t.annual.signatures.incomplete}
        </span>
        {panel.deadline && (
          <span className="muted sm">
            {t.annual.signatures.deadline}: {written(panel.deadline, locale)}
            {panel.daysLeft !== null && ` · ${t.annual.year.daysLeft(panel.daysLeft)}`}
          </span>
        )}
      </div>

      {panel.statementFileId === null ? (
        <div className="alert a-warn">
          <Icon.Warn />
          <div>
            <b>{t.annual.signatures.noStatement}</b>
          </div>
        </div>
      ) : (
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th>{t.annual.signatures.person}</th>
                <th>{t.annual.signatures.function}</th>
                <th>{t.annual.signatures.state}</th>
                <th>{t.annual.signatures.method}</th>
                <th>{t.annual.signatures.when}</th>
              </tr>
            </thead>
            <tbody>
              {panel.required.map((signer) => (
                <tr key={signer.officerId ?? signer.capacity}>
                  <td>{signer.name}</td>
                  <td className="muted sm">
                    {signer.role ?? t.annual.signatures.capacities.bookkeeper}
                  </td>
                  <td>
                    <span className={`chip ${signer.state === 'missing' ? 'st-warn' : 'st-ok'}`}>
                      <i />
                      {t.annual.signatures.states[signer.state]}
                    </span>
                  </td>
                  <td className="muted sm">{methodLabel(t, signer.method)}</td>
                  <td className="doc">{signer.signedAt ? written(signer.signedAt, locale) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {panel.extra.length > 0 && (
        <div className="alert a-warn">
          <Icon.Warn />
          <div>
            <b>{t.annual.signatures.extra}</b>
            <p>{panel.extra.map((signature) => signature.personName).join(', ')}</p>
          </div>
        </div>
      )}

      {/* Zdania z serwera o tym, czego nie sprawdziliśmy — przepisane co do słowa, nie streszczone */}
      {panel.notes.map((text) => (
        <p className="hint" key={text}>
          {text}
        </p>
      ))}

      {notice && (
        <p className="hint" role="status">
          {notice}
        </p>
      )}

      {canSign && panel.statementFileId !== null && (
        <>
          <div className="bar2">
            <button
              type="button"
              className="btn sec"
              disabled={busy}
              onClick={() => void run(async () => saveFile(await api.fileToSign(companyId, report.id)))}
            >
              <Icon.Download />
              {t.annual.signatures.downloadToSign}
            </button>
          </div>
          <p className="hint">{t.annual.signatures.downloadHint}</p>

          <h4>{t.annual.signatures.upload}</h4>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (!file) return;
              void run(async () => {
                await api.addSignature(companyId, report.id, file, {
                  capacity,
                  method,
                  officerId: capacity === 'BOARD_MEMBER' ? officerId : undefined,
                  personName: capacity === 'BOOKKEEPER' ? personName : undefined,
                  note: note.trim() === '' ? undefined : note,
                });
                setFile(null);
                setNote('');
              });
            }}
          >
            <div className="fields">
              <div className="field">
                <label htmlFor="capacity">{t.annual.signatures.capacity}</label>
                <select
                  id="capacity"
                  className="inp"
                  value={capacity}
                  onChange={(event) => setCapacity(event.target.value as SignatureCapacity)}
                >
                  <option value="BOARD_MEMBER">{t.annual.signatures.capacities.board_member}</option>
                  <option value="BOOKKEEPER">{t.annual.signatures.capacities.bookkeeper}</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="method">{t.annual.signatures.method}</label>
                <select
                  id="method"
                  className="inp"
                  value={method}
                  onChange={(event) => setMethod(event.target.value as SignatureMethod)}
                >
                  <option value="QUALIFIED">{t.annual.signatures.methods.qualified}</option>
                  <option value="TRUSTED_PROFILE">{t.annual.signatures.methods.trusted_profile}</option>
                  <option value="PERSONAL">{t.annual.signatures.methods.personal}</option>
                  <option value="STATEMENT">{t.annual.signatures.methods.statement}</option>
                  <option value="REFUSAL">{t.annual.signatures.methods.refusal}</option>
                </select>
              </div>
              {capacity === 'BOARD_MEMBER' ? (
                <div className="field">
                  <label htmlFor="officer">{t.annual.signatures.officer}</label>
                  <select
                    id="officer"
                    className="inp"
                    value={officerId}
                    onChange={(event) => setOfficerId(event.target.value)}
                    required
                  >
                    <option value="">—</option>
                    {officers.map((officer) => (
                      <option key={officer.id} value={officer.id}>
                        {officer.firstName} {officer.lastName} · {officer.roleLabel}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="field">
                  <label htmlFor="personName">{t.annual.signatures.personName}</label>
                  <input
                    id="personName"
                    className="inp"
                    value={personName}
                    onChange={(event) => setPersonName(event.target.value)}
                    required
                  />
                </div>
              )}
              <div className="field">
                <label htmlFor="signatureNote">{t.annual.signatures.note}</label>
                <input
                  id="signatureNote"
                  className="inp"
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="signatureFile">{t.annual.signatures.upload}</label>
                {/* Bez `required`: pilnuje tego wyłączony przycisk, a nie chmurka przeglądarki —
                    i tylko tak widać z ekranu, czego jeszcze brakuje do wysłania */}
                <input
                  id="signatureFile"
                  className="inp"
                  type="file"
                  onChange={(event) => setFile(event.target.files?.[0] ?? null)}
                />
              </div>
            </div>
            <div className="bar2">
              <button type="submit" className="btn" disabled={busy || !file}>
                {t.annual.signatures.send}
              </button>
            </div>
          </form>

          <h4>{t.annual.signatures.declaration.title}</h4>
          <p className="hint">{t.annual.signatures.declaration.hint}</p>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void run(async () => {
                const generated = await api.declaration(
                  companyId,
                  report.id,
                  declarationOfficer,
                  declarationKind,
                  declarationNote.trim() === '' ? undefined : declarationNote,
                );
                setNotice(t.annual.signatures.declaration.generated(generated.fileName));
                setDeclarationNote('');
              });
            }}
          >
            <div className="fields">
              <div className="field">
                <label htmlFor="declarationOfficer">{t.annual.signatures.declaration.who}</label>
                <select
                  id="declarationOfficer"
                  className="inp"
                  value={declarationOfficer}
                  onChange={(event) => setDeclarationOfficer(event.target.value)}
                  required
                >
                  <option value="">—</option>
                  {officers.map((officer) => (
                    <option key={officer.id} value={officer.id}>
                      {officer.firstName} {officer.lastName} · {officer.roleLabel}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="declarationKind">{t.annual.signatures.declaration.kind}</label>
                <select
                  id="declarationKind"
                  className="inp"
                  value={declarationKind}
                  onChange={(event) => setDeclarationKind(event.target.value as 'STATEMENT' | 'REFUSAL')}
                >
                  <option value="STATEMENT">{t.annual.signatures.declaration.statement}</option>
                  <option value="REFUSAL">{t.annual.signatures.declaration.refusal}</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="declarationNote">{t.annual.signatures.declaration.justification}</label>
                <input
                  id="declarationNote"
                  className="inp"
                  value={declarationNote}
                  onChange={(event) => setDeclarationNote(event.target.value)}
                  required={declarationKind === 'REFUSAL'}
                />
              </div>
            </div>
            <div className="bar2">
              <button type="submit" className="btn sec" disabled={busy || declarationOfficer === ''}>
                {t.annual.signatures.declaration.generate}
              </button>
            </div>
          </form>
        </>
      )}
    </>
  );
}

/** Uchwały i protokół (docs/08, krok 9). Kwoty przychodzą z planu, więc nie ma ich do wpisania. */
function ResolutionsTab({
  companyId,
  report,
  editable,
  t,
  locale,
  onChanged,
}: {
  companyId: string;
  report: AnnualReport;
  editable: boolean;
  t: Dictionary;
  locale: Locale;
  onChanged: () => Promise<void>;
}) {
  const [plan, setPlan] = useState<ResolutionPlan | null>(null);
  const [resolutions, setResolutions] = useState<Resolution[]>([]);
  const [adopting, setAdopting] = useState<Record<string, { number: string; adoptedOn: string }>>({});
  const [minutes, setMinutes] = useState({
    meetingDate: '',
    place: '',
    chairman: '',
    secretary: '',
    unanimous: true,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Failure | null>(null);

  const load = useCallback(async () => {
    try {
      const [proposals, existing] = await Promise.all([
        api.resolutionPlan(companyId, report.id),
        api.resolutions(companyId, report.id),
      ]);
      setPlan(proposals);
      setResolutions(existing);
    } catch (failure) {
      setError(failureOf(failure));
    }
  }, [companyId, report.id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
      await onChanged();
    } catch (failure) {
      setError(failureOf(failure));
    } finally {
      setBusy(false);
    }
  }

  if (!plan) return <p className="muted">{t.loading}</p>;

  const hasAdopted = resolutions.some((resolution) => resolution.status === 'adopted');
  const hasMinutes = report.files.some((file) => file.role === 'minutes');

  return (
    <>
      <ErrorAlert error={error} t={t} />
      <h4>{t.annual.resolutions.title}</h4>
      <p className="hint">{t.annual.resolutions.lead}</p>

      {plan.gaps.length > 0 && (
        <div className="alert a-warn">
          <Icon.Warn />
          <div>
            <b>{t.annual.resolutions.gaps}</b>
            <p>{plan.gaps.join(' · ')}</p>
          </div>
        </div>
      )}

      <div className="tbl">
        <table>
          <thead>
            <tr>
              <th>{t.annual.resolutions.title}</th>
              <th>{t.annual.resolutions.reason}</th>
              <th>{t.annual.year.state}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {plan.proposals.map((proposal) => {
              const existing = proposal.existingId
                ? resolutions.find((resolution) => resolution.id === proposal.existingId)
                : undefined;
              const adoption = existing ? adopting[existing.id] : undefined;
              return (
                <tr key={proposal.kind}>
                  <td>{proposal.title}</td>
                  <td className="muted sm">{proposal.reason}</td>
                  <td>
                    {existing ? (
                      <span className={`chip ${existing.status === 'adopted' ? 'st-ok' : 'st-draft'}`}>
                        <i />
                        {existing.status === 'adopted'
                          ? `${t.annual.resolutions.adopted}${existing.adoptedOn ? ` · ${written(existing.adoptedOn, locale)}` : ''}`
                          : t.annual.resolutions.draft}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="act">
                    <div className="row">
                      {editable && !existing && (
                        <button
                          type="button"
                          className="btn ghost sm"
                          disabled={busy}
                          onClick={() =>
                            void run(() =>
                              api.createResolution(companyId, {
                                organ: proposal.organ,
                                kind: proposal.kind,
                                fiscalYearId: report.fiscalYearId,
                                annualReportId: report.id,
                                params: proposal.params,
                              }),
                            )
                          }
                        >
                          <Icon.Plus />
                          {t.annual.resolutions.create}
                        </button>
                      )}
                      {existing && (
                        <button
                          type="button"
                          className="btn ghost sm"
                          disabled={busy}
                          onClick={() =>
                            void run(async () => saveFile(await api.resolutionPdf(companyId, existing.id)))
                          }
                        >
                          <Icon.Download />
                          {t.annual.resolutions.pdf}
                        </button>
                      )}
                      {editable && existing && existing.status === 'draft' && (
                        <button
                          type="button"
                          className="btn ghost sm"
                          disabled={busy}
                          onClick={() =>
                            setAdopting((current) => ({
                              ...current,
                              [existing.id]: current[existing.id] ?? { number: '', adoptedOn: '' },
                            }))
                          }
                        >
                          {t.annual.resolutions.adopt}
                        </button>
                      )}
                    </div>
                    {adoption && existing && (
                      <form
                        onSubmit={(event) => {
                          event.preventDefault();
                          void run(async () => {
                            await api.adoptResolution(
                              companyId,
                              existing.id,
                              adoption.adoptedOn,
                              adoption.number === '' ? null : adoption.number,
                            );
                            setAdopting((current) => {
                              const next = { ...current };
                              delete next[existing.id];
                              return next;
                            });
                          });
                        }}
                      >
                        <div className="fields">
                          <div className="field">
                            <label htmlFor={`n-${existing.id}`}>{t.annual.resolutions.number}</label>
                            <input
                              id={`n-${existing.id}`}
                              className="inp"
                              value={adoption.number}
                              onChange={(event) =>
                                setAdopting((current) => ({
                                  ...current,
                                  [existing.id]: { ...adoption, number: event.target.value },
                                }))
                              }
                            />
                          </div>
                          <div className="field">
                            <label htmlFor={`d-${existing.id}`}>{t.annual.resolutions.adoptedOn}</label>
                            <input
                              id={`d-${existing.id}`}
                              className="inp"
                              type="date"
                              value={adoption.adoptedOn}
                              onChange={(event) =>
                                setAdopting((current) => ({
                                  ...current,
                                  [existing.id]: { ...adoption, adoptedOn: event.target.value },
                                }))
                              }
                              required
                            />
                          </div>
                        </div>
                        <div className="bar2">
                          <button type="submit" className="btn sm" disabled={busy || adoption.adoptedOn === ''}>
                            {t.annual.resolutions.adopt}
                          </button>
                        </div>
                      </form>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <h4>{t.annual.resolutions.minutes.title}</h4>
      {!hasAdopted && <p className="hint">{t.annual.resolutions.minutes.needsResolutions}</p>}
      {editable && hasAdopted && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void run(() =>
              api.generateMinutes(companyId, report.id, {
                meetingDate: minutes.meetingDate,
                place: minutes.place,
                chairman: minutes.chairman === '' ? undefined : minutes.chairman,
                secretary: minutes.secretary === '' ? undefined : minutes.secretary,
                unanimous: minutes.unanimous,
              }),
            );
          }}
        >
          <div className="fields">
            <div className="field">
              <label htmlFor="meetingDate">{t.annual.resolutions.minutes.date}</label>
              <input
                id="meetingDate"
                className="inp"
                type="date"
                value={minutes.meetingDate}
                onChange={(event) => setMinutes({ ...minutes, meetingDate: event.target.value })}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="meetingPlace">{t.annual.resolutions.minutes.place}</label>
              <input
                id="meetingPlace"
                className="inp"
                value={minutes.place}
                onChange={(event) => setMinutes({ ...minutes, place: event.target.value })}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="chairman">{t.annual.resolutions.minutes.chairman}</label>
              <input
                id="chairman"
                className="inp"
                value={minutes.chairman}
                onChange={(event) => setMinutes({ ...minutes, chairman: event.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="secretary">{t.annual.resolutions.minutes.secretary}</label>
              <input
                id="secretary"
                className="inp"
                value={minutes.secretary}
                onChange={(event) => setMinutes({ ...minutes, secretary: event.target.value })}
              />
            </div>
          </div>
          <div className="bar2">
            <label className="check">
              <input
                type="checkbox"
                checked={minutes.unanimous}
                onChange={(event) => setMinutes({ ...minutes, unanimous: event.target.checked })}
              />
              {t.annual.resolutions.minutes.unanimous}
            </label>
            <div className="sp">
              <button type="submit" className="btn" disabled={busy}>
                {t.annual.resolutions.minutes.generate}
              </button>
              {hasMinutes && (
                <button
                  type="button"
                  className="btn sec"
                  disabled={busy}
                  onClick={() => void run(async () => saveFile(await api.minutesFile(companyId, report.id)))}
                >
                  <Icon.Download />
                  {t.annual.resolutions.minutes.download}
                </button>
              )}
            </div>
          </div>
        </form>
      )}
    </>
  );
}

/** Komplet do repozytorium, zapis złożenia i wzmianki w rejestrze (docs/08, kroki 10 i sekcja 8). */
function KrsTab({
  companyId,
  report,
  editable,
  t,
  locale,
  onChanged,
}: {
  companyId: string;
  report: AnnualReport;
  editable: boolean;
  t: Dictionary;
  locale: Locale;
  onChanged: () => Promise<void>;
}) {
  const [pack, setPack] = useState<RdfPackage | null>(null);
  const [register, setRegister] = useState<RegisterStatus | null>(null);
  const [filing, setFiling] = useState({ submittedOn: '', submissionNumber: '' });
  const [confirmation, setConfirmation] = useState({ confirmedOn: '', file: null as File | null });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Failure | null>(null);

  const load = useCallback(async () => {
    try {
      const [komplet, rejestr] = await Promise.all([
        api.rdfPackage(companyId, report.id),
        api.krsRegister(companyId),
      ]);
      setPack(komplet);
      setRegister(rejestr);
    } catch (failure) {
      setError(failureOf(failure));
    }
  }, [companyId, report.id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
      await onChanged();
    } catch (failure) {
      setError(failureOf(failure));
    } finally {
      setBusy(false);
    }
  }

  if (!pack) return <p className="muted">{t.loading}</p>;

  return (
    <>
      <ErrorAlert error={error} t={t} />
      <h4>{t.annual.krs.title}</h4>
      <p className="hint">{t.annual.krs.lead}</p>

      <div className="row">
        <span className={`chip ${pack.ready ? 'st-ok' : 'st-warn'}`}>
          <i />
          {pack.ready ? t.annual.krs.ready : t.annual.krs.notReady}
        </span>
      </div>

      {pack.deadlines.zlozenie && (
        <p className="hint">
          {t.annual.krs.deadlines}: {t.annual.year.obligations.krs_filing.toLowerCase()}{' '}
          {written(pack.deadlines.zlozenie, locale)}
          {pack.deadlines.zatwierdzoneDnia && ` · ${t.annual.statuses.approved.toLowerCase()} ${written(pack.deadlines.zatwierdzoneDnia, locale)}`}
        </p>
      )}

      <h4>{t.annual.krs.checklist}</h4>
      <div className="tbl">
        <table>
          <thead>
            <tr>
              <th>{t.annual.krs.checklist}</th>
              <th>{t.annual.year.state}</th>
              <th>{t.annual.krs.required}</th>
            </tr>
          </thead>
          <tbody>
            {pack.items.map((item) => (
              <tr key={item.role}>
                <td>
                  {item.label}
                  {item.note && <p className="muted sm">{item.note}</p>}
                </td>
                <td>
                  <span className={`chip ${item.present ? 'st-ok' : item.required ? 'st-warn' : 'st-draft'}`}>
                    <i />
                    {item.present ? t.annual.krs.present : t.annual.krs.missing}
                  </span>
                </td>
                <td className="muted sm">{item.required ? t.annual.krs.required : t.annual.krs.optional}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pack.people.length > 0 && (
        <>
          <h4>{t.annual.krs.people}</h4>
          <ul>
            {pack.people.map((person) => (
              <li key={person.name}>
                {person.name} · {person.role}
                {person.peselInKrs ? (
                  <span className="chip st-ok">
                    <i />
                    {t.annual.krs.pesel}
                  </span>
                ) : (
                  <span className="chip st-warn">
                    <i />
                    {t.annual.krs.pesel}: —
                  </span>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      {pack.notes.map((note) => (
        <p className="hint" key={note}>
          {note}
        </p>
      ))}

      <h4>{t.annual.krs.instructions}</h4>
      <ol>
        {pack.instructions.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>

      <div className="bar2">
        <button
          type="button"
          className="btn sec"
          disabled={busy}
          onClick={() => void run(async () => saveFile(await api.rdfPackageZip(companyId, report.id)))}
        >
          <Icon.Download />
          {t.annual.krs.zip}
        </button>
      </div>

      {editable && (
        <>
          <h4>{t.annual.krs.filing.title}</h4>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void run(async () => {
                await api.recordFiling(companyId, report.id, {
                  submittedOn: filing.submittedOn,
                  submissionNumber: filing.submissionNumber === '' ? null : filing.submissionNumber,
                });
                setFiling({ submittedOn: '', submissionNumber: '' });
              });
            }}
          >
            <div className="fields">
              <div className="field">
                <label htmlFor="submittedOn">{t.annual.krs.filing.submittedOn}</label>
                <input
                  id="submittedOn"
                  className="inp"
                  type="date"
                  value={filing.submittedOn}
                  onChange={(event) => setFiling({ ...filing, submittedOn: event.target.value })}
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="submissionNumber">{t.annual.krs.filing.number}</label>
                <input
                  id="submissionNumber"
                  className="inp"
                  value={filing.submissionNumber}
                  onChange={(event) => setFiling({ ...filing, submissionNumber: event.target.value })}
                />
              </div>
            </div>
            <div className="bar2">
              <button type="submit" className="btn" disabled={busy || filing.submittedOn === ''}>
                {t.annual.krs.filing.record}
              </button>
            </div>
          </form>

          {pack.filings.length > 0 && (
            <div className="tbl">
              <table>
                <thead>
                  <tr>
                    <th>{t.annual.krs.filing.submittedOn}</th>
                    <th>{t.annual.krs.filing.number}</th>
                    <th>{t.annual.year.state}</th>
                  </tr>
                </thead>
                <tbody>
                  {pack.filings.map((item) => (
                    <tr key={item.id}>
                      <td className="doc">{written(item.submittedOn, locale)}</td>
                      <td className="mono sm">{item.submissionNumber ?? '—'}</td>
                      <td className="muted sm">
                        {item.unapproved && t.annual.krs.filing.unapproved}
                        {item.confirmedOn && ` · ${written(item.confirmedOn, locale)}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h4>{t.annual.krs.confirmation.title}</h4>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (!confirmation.file) return;
              void run(async () => {
                await api.uploadConfirmation(
                  companyId,
                  report.id,
                  confirmation.file as File,
                  confirmation.confirmedOn,
                );
                setConfirmation({ confirmedOn: '', file: null });
              });
            }}
          >
            <div className="fields">
              <div className="field">
                <label htmlFor="confirmedOn">{t.annual.krs.confirmation.confirmedOn}</label>
                <input
                  id="confirmedOn"
                  className="inp"
                  type="date"
                  value={confirmation.confirmedOn}
                  onChange={(event) => setConfirmation({ ...confirmation, confirmedOn: event.target.value })}
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="confirmationFile">{t.annual.krs.confirmation.upload}</label>
                <input
                  id="confirmationFile"
                  className="inp"
                  type="file"
                  onChange={(event) => setConfirmation({ ...confirmation, file: event.target.files?.[0] ?? null })}
                />
              </div>
            </div>
            <div className="bar2">
              <button
                type="submit"
                className="btn"
                disabled={busy || !confirmation.file || confirmation.confirmedOn === ''}
              >
                {t.annual.krs.confirmation.upload}
              </button>
            </div>
          </form>
        </>
      )}

      <h4>{t.annual.krs.register.title}</h4>
      <p className="hint">{t.annual.krs.register.lead}</p>
      {register && (
        <>
          <div className="row">
            <span className="muted sm">
              {t.annual.krs.register.number}: <span className="mono">{register.krs ?? '—'}</span>
            </span>
            <span className="muted sm">
              {t.annual.krs.register.checkedAt}:{' '}
              {register.checkedAt ? written(register.checkedAt, locale) : t.annual.krs.register.never}
            </span>
            <button
              type="button"
              className="btn ghost sm"
              disabled={busy || register.krs === null}
              // Bez `force`: limit „raz na dobę" pilnuje serwer, a nie przycisk — dziesięć kliknięć
              // nie zamienia się w dziesięć pytań do ministerstwa
              onClick={() => void run(() => api.checkKrsRegister(companyId))}
            >
              {t.annual.krs.register.check}
            </button>
          </div>

          {register.krs === null && <p className="hint">{t.annual.krs.register.noNumber}</p>}

          {register.missingYears.length > 0 && (
            <div className="alert a-warn">
              <Icon.Warn />
              <div>
                <b>{t.annual.krs.register.missingYears}</b>
                <p>{register.missingYears.join(', ')}</p>
              </div>
            </div>
          )}

          {register.mentions.length === 0 ? (
            <p className="hint">{t.annual.krs.register.none}</p>
          ) : (
            <div className="tbl">
              <table>
                <thead>
                  <tr>
                    <th>{t.annual.krs.register.period}</th>
                    <th>{t.annual.krs.register.filedOn}</th>
                    <th>{t.annual.krs.register.approval}</th>
                    <th>{t.annual.krs.register.activity}</th>
                    <th>{t.annual.krs.register.audit}</th>
                  </tr>
                </thead>
                <tbody>
                  {register.mentions.map((mention) => (
                    <tr key={`${mention.periodFrom}-${mention.periodTo}`}>
                      <td className="doc">
                        {written(mention.periodFrom, locale)} — {written(mention.periodTo, locale)}
                      </td>
                      <td className="doc">
                        {mention.statementFiledOn ? written(mention.statementFiledOn, locale) : '—'}
                      </td>
                      <td>{mention.hasApprovalResolution ? '✓' : '—'}</td>
                      <td>{mention.hasActivityReport ? '✓' : '—'}</td>
                      <td>{mention.hasAuditReport ? '✓' : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </>
  );
}

/** Kartoteki zarządu i wspólników — okresami, bo uchwały za dany rok dotyczą tamtych osób. */
function RegistersTab({
  companyId,
  editable,
  t,
  locale,
}: {
  companyId: string;
  editable: boolean;
  t: Dictionary;
  locale: Locale;
}) {
  const [officers, setOfficers] = useState<Officer[]>([]);
  const [shareholders, setShareholders] = useState<Shareholder[]>([]);
  const [officer, setOfficer] = useState({
    firstName: '',
    lastName: '',
    role: 'BOARD_MEMBER' as OfficerRole,
    fromDate: '',
    peselInKrs: false,
  });
  const [shareholder, setShareholder] = useState({ name: '', shares: '', nominalValue: '', fromDate: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Failure | null>(null);

  const load = useCallback(async () => {
    try {
      const [board, owners] = await Promise.all([api.officers(companyId), api.shareholders(companyId)]);
      setOfficers(board);
      setShareholders(owners);
    } catch (failure) {
      setError(failureOf(failure));
    }
  }, [companyId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
    } catch (failure) {
      setError(failureOf(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <ErrorAlert error={error} t={t} />

      <h4>{t.annual.registers.officers.title}</h4>
      <p className="hint">{t.annual.registers.officers.lead}</p>
      {officers.length === 0 ? (
        <div className="alert a-warn">
          <Icon.Warn />
          <div>
            <b>{t.annual.registers.officers.none}</b>
            <p>{t.annual.registers.officers.peselHint}</p>
          </div>
        </div>
      ) : (
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th>{t.annual.registers.officers.lastName}</th>
                <th>{t.annual.registers.officers.role}</th>
                <th>{t.annual.registers.officers.from}</th>
                <th>{t.annual.registers.officers.to}</th>
                <th>{t.annual.registers.officers.pesel}</th>
              </tr>
            </thead>
            <tbody>
              {officers.map((item) => (
                <tr key={item.id}>
                  <td>
                    {item.firstName} {item.lastName}
                  </td>
                  <td className="muted sm">{item.roleLabel}</td>
                  <td className="doc">{written(item.fromDate, locale)}</td>
                  <td className="doc">{item.toDate ? written(item.toDate, locale) : '—'}</td>
                  <td>{item.peselInKrs ? '✓' : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editable && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void run(async () => {
              await api.addOfficer(companyId, officer);
              setOfficer({ ...officer, firstName: '', lastName: '' });
            });
          }}
        >
          <div className="fields">
            <div className="field">
              <label htmlFor="oFirst">{t.annual.registers.officers.firstName}</label>
              <input
                id="oFirst"
                className="inp"
                value={officer.firstName}
                onChange={(event) => setOfficer({ ...officer, firstName: event.target.value })}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="oLast">{t.annual.registers.officers.lastName}</label>
              <input
                id="oLast"
                className="inp"
                value={officer.lastName}
                onChange={(event) => setOfficer({ ...officer, lastName: event.target.value })}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="oRole">{t.annual.registers.officers.role}</label>
              <select
                id="oRole"
                className="inp"
                value={officer.role}
                onChange={(event) => setOfficer({ ...officer, role: event.target.value as OfficerRole })}
              >
                {(['PRESIDENT', 'BOARD_MEMBER', 'PROXY', 'LIQUIDATOR'] as OfficerRole[]).map((role) => (
                  <option key={role} value={role}>
                    {t.annual.registers.officers.roles[role]}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="oFrom">{t.annual.registers.officers.from}</label>
              <input
                id="oFrom"
                className="inp"
                type="date"
                value={officer.fromDate}
                onChange={(event) => setOfficer({ ...officer, fromDate: event.target.value })}
                required
              />
            </div>
          </div>
          <div className="bar2">
            <label className="check">
              <input
                type="checkbox"
                checked={officer.peselInKrs}
                onChange={(event) => setOfficer({ ...officer, peselInKrs: event.target.checked })}
              />
              {t.annual.registers.officers.pesel}
            </label>
            <div className="sp">
              <button type="submit" className="btn sec" disabled={busy}>
                <Icon.Plus />
                {t.annual.registers.officers.add}
              </button>
            </div>
          </div>
          <p className="hint">{t.annual.registers.officers.peselHint}</p>
        </form>
      )}

      <h4>{t.annual.registers.shareholders.title}</h4>
      <p className="hint">{t.annual.registers.shareholders.lead}</p>
      {shareholders.length === 0 ? (
        <p className="hint">{t.annual.registers.shareholders.none}</p>
      ) : (
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th>{t.annual.registers.shareholders.name}</th>
                <th className="n">{t.annual.registers.shareholders.shares}</th>
                <th className="n">{t.annual.registers.shareholders.votes}</th>
                <th className="n">{t.annual.registers.shareholders.nominal}</th>
                <th className="n">{t.annual.registers.shareholders.capital}</th>
              </tr>
            </thead>
            <tbody>
              {shareholders.map((item) => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td className="n">{item.shares}</td>
                  <td className="n">{item.votes}</td>
                  <td className="n">{item.nominalValue}</td>
                  <td className="n">{item.capital}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editable && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void run(async () => {
              await api.addShareholder(companyId, {
                name: shareholder.name,
                shares: Number(shareholder.shares),
                nominalValue: shareholder.nominalValue,
                fromDate: shareholder.fromDate,
              });
              setShareholder({ name: '', shares: '', nominalValue: '', fromDate: shareholder.fromDate });
            });
          }}
        >
          <div className="fields">
            <div className="field">
              <label htmlFor="shName">{t.annual.registers.shareholders.name}</label>
              <input
                id="shName"
                className="inp"
                value={shareholder.name}
                onChange={(event) => setShareholder({ ...shareholder, name: event.target.value })}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="shShares">{t.annual.registers.shareholders.shares}</label>
              <input
                id="shShares"
                className="inp"
                inputMode="numeric"
                value={shareholder.shares}
                onChange={(event) => setShareholder({ ...shareholder, shares: event.target.value })}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="shNominal">{t.annual.registers.shareholders.nominal}</label>
              <input
                id="shNominal"
                className="inp"
                inputMode="decimal"
                value={shareholder.nominalValue}
                onChange={(event) => setShareholder({ ...shareholder, nominalValue: event.target.value })}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="shFrom">{t.annual.registers.officers.from}</label>
              <input
                id="shFrom"
                className="inp"
                type="date"
                value={shareholder.fromDate}
                onChange={(event) => setShareholder({ ...shareholder, fromDate: event.target.value })}
                required
              />
            </div>
          </div>
          <div className="bar2">
            <button type="submit" className="btn sec" disabled={busy}>
              <Icon.Plus />
              {t.annual.registers.shareholders.add}
            </button>
          </div>
        </form>
      )}
    </>
  );
}

/**
 * Nazwa sposobu podpisu.
 *
 * Kod przychodzi z serwera jako tekst, więc szukamy go w słowniku po kluczu; nieznany kod pokazujemy
 * takim, jaki przyszedł, zamiast zgadywać — nowy sposób podpisu doda się kiedyś po stronie API
 * i lepiej, żeby ekran pokazał go surowo, niż udawał, że go nie ma.
 */
function methodLabel(t: Dictionary, method: string | null): string {
  if (method === null) return '—';
  return (t.annual.signatures.methods as Record<string, string>)[method] ?? method;
}

/** Kolor stanu: zielony dopiero wtedy, gdy sprawa jest domknięta wobec rejestru. */
function statusChip(status: AnnualReport['status']): string {
  switch (status) {
    case 'confirmed':
      return 'st-ok';
    case 'filed':
    case 'approved':
    case 'signed':
      return 'st-info';
    case 'filed_unapproved':
      return 'st-warn';
    case 'abandoned':
      return 'st-bad';
    default:
      return 'st-draft';
  }
}

function yearLabel(year: FiscalYear): string {
  return year.startsOn.slice(0, 4) === year.endsOn.slice(0, 4)
    ? year.startsOn.slice(0, 4)
    : `${year.startsOn} — ${year.endsOn}`;
}

/** Ostatni zakończony rok obrotowy: ten, o którym mówi się „sprawozdanie za miniony rok". */
function lastEnded(years: FiscalYear[]): FiscalYear | null {
  const today = new Date().toISOString().slice(0, 10);
  return years.filter((year) => year.endsOn < today).sort((a, b) => b.endsOn.localeCompare(a.endsOn))[0] ?? null;
}
